import { ChangeEvent, InputHTMLAttributes, useState } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { Form, InputGroup } from "react-bootstrap";

import MobileSelect from "./MobileSelect";
import ReactDatePicker from "react-datepicker";
import { SafeSelect } from "./Safe/SafeStyled";
import classNames from "classnames";
import styled from "styled-components";

// Mobile zoom prevention utilities
const preventMobileZoom = () => {
  // Store the current scroll position
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;

  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
    );
  }

  // Restore scroll position
  window.scrollTo(scrollX, scrollY);
};

const resetMobileZoom = () => {
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes"
    );
  }

  // Multiple strategies to force zoom reset
  setTimeout(() => {
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    // Strategy 1: Small scroll to force browser to recalculate viewport
    window.scrollTo(scrollX, scrollY + 1);

    // Strategy 2: Force a repaint by toggling body height
    requestAnimationFrame(() => {
      const body = document.body;
      const originalHeight = body.style.height;
      body.style.height = `${body.scrollHeight}px`;

      requestAnimationFrame(() => {
        body.style.height = originalHeight;
        // Restore original scroll position
        window.scrollTo(scrollX, scrollY);
      });
    });
  }, 100);

  // Strategy 3: Ensure scroll position is maintained
  setTimeout(() => {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) {
      // Maintain scroll position
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      window.scrollTo(scrollX, scrollY);
    }
  }, 150);
};

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

interface PasswordInputProps {
  name: string;
  placeholder?: string;
  refCallback?: any;
  errors: FieldErrors;
  control?: Control<any>;
  register?: any;
  className?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

const SafePsw = styled.div`
  background-color: ${(props) => props.theme.secondColor};
`;

const SafeEye = styled.div`
  color: ${(props) => props.theme.mainColor};
`;

/* Password Input */
const PasswordInput = ({
  name,
  placeholder,
  refCallback,
  errors,
  control,
  register,
  className,
  onChange,
}: PasswordInputProps) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleFocus = () => {
    if (isMobileDevice()) {
      preventMobileZoom();
    }
  };

  const handleBlur = () => {
    if (isMobileDevice()) {
      resetMobileZoom();
    }
  };

  return (
    <>
      <InputGroup className="mb-0">
        <Form.Control
          type={showPassword ? "text" : "password"}
          placeholder={placeholder}
          name={name}
          id={name}
          as="input"
          ref={(r: HTMLInputElement) => {
            // merge RHF ref with local ref
            const registered = register ? register(name) : undefined;
            registered?.ref?.(r);
            if (refCallback) refCallback(r);
          }}
          className={className}
          isInvalid={errors && errors[name] ? true : false}
          {...(register ? register(name) : {})}
          autoComplete={name}
          onChange={(e: any) => {
            const registered = register ? register(name) : undefined;
            registered?.onChange?.(e);
            if (onChange) onChange(e);
          }}
          onFocus={handleFocus}
          onBlur={(e: any) => {
            const registered = register ? register(name) : undefined;
            registered?.onBlur?.(e);
            handleBlur();
          }}
        />
        <SafePsw
          className={classNames("input-group-text", "input-group-password", {
            "show-password": showPassword,
          })}
          data-password={showPassword ? "true" : "false"}
        >
          <SafeEye
            className="password-eye"
            onClick={() => {
              setShowPassword(!showPassword);
            }}
          ></SafeEye>
        </SafePsw>
      </InputGroup>
    </>
  );
};

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  type?: string;
  name: string;
  placeholder?: string;
  register?: any;
  errors?: FieldErrors;
  control?: Control<any>;
  className?: string;
  labelClassName?: string;
  containerClass?: string;
  refCallback?: any;
  children?: any;
  rows?: string;
  options?: IOptions[];
}

interface IOptions {
  value: string;
  label: string;
}

const FormInput = ({
  label,
  type,
  name,
  placeholder,
  register,
  errors,
  control,
  className,
  labelClassName,
  containerClass,
  refCallback,
  children,
  options,
  rows,
  onChange,
  ...otherProps
}: FormInputProps) => {
  // handle input type
  const comp =
    type === "textarea" ? "textarea" : type === "select" ? "select" : "input";

  // Mobile zoom handlers
  const handleFocus = () => {
    if (isMobileDevice()) {
      preventMobileZoom();
    }
  };

  const handleBlur = () => {
    if (isMobileDevice()) {
      resetMobileZoom();
    }
  };

  return (
    <>
      {type === "hidden" ? (
        <input
          type={type}
          name={name}
          {...(register ? register(name) : {})}
          {...otherProps}
        />
      ) : (
        <>
          {type === "password" ? (
            <>
              <Form.Group className={containerClass}>
                {label ? (
                  <>
                    <Form.Label className={labelClassName}>{label}</Form.Label>{" "}
                    {children}{" "}
                  </>
                ) : null}
                <PasswordInput
                  name={name}
                  placeholder={placeholder}
                  refCallback={refCallback}
                  errors={errors!}
                  register={register}
                  className={className}
                  onChange={onChange}
                />

                {errors && errors[name] ? (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors[name]["message"]}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>
            </>
          ) : (
            <>
              {type === "checkbox" || type === "radio" ? (
                <>
                  <Form.Group className={containerClass}>
                    <SafeSelect
                      type={type}
                      label={label}
                      name={name}
                      id={(otherProps as any)?.id ?? name}
                      ref={(r: HTMLInputElement) => {
                        const registered = register
                          ? register(name)
                          : undefined;
                        registered?.ref?.(r);
                        if (refCallback) refCallback(r);
                      }}
                      className={className}
                      isInvalid={errors && errors[name] ? true : false}
                      {...(register ? register(name) : {})}
                      {...otherProps}
                      onChange={(e: any) => {
                        const registered = register
                          ? register(name)
                          : undefined;
                        registered?.onChange?.(e);
                        if (onChange) (onChange as any)(e);
                      }}
                      onFocus={handleFocus}
                      onBlur={(e: any) => {
                        const registered = register
                          ? register(name)
                          : undefined;
                        registered?.onBlur?.(e);
                        handleBlur();
                      }}
                    />
                    {errors && errors[name] ? (
                      <Form.Control.Feedback type="invalid">
                        {errors[name]["message"]}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                </>
              ) : (
                <>
                  {type === "birthdate" ? (
                    <Form.Group className={containerClass}>
                      {label ? (
                        <Form.Label className={labelClassName}>
                          {label}
                        </Form.Label>
                      ) : null}
                      <Controller
                        control={control}
                        name={name}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <ReactDatePicker
                            onChange={(d) => {
                              onChange(d);
                              handleBlur();
                            }}
                            onCalendarOpen={handleFocus}
                            onCalendarClose={handleBlur}
                            onBlur={(e) => {
                              onBlur();
                              handleBlur();
                            }}
                            onFocus={handleFocus}
                            selected={value}
                          />
                        )}
                      />
                      {errors && errors[name] ? (
                        <Form.Control.Feedback type="invalid">
                          {errors[name]["message"]}
                        </Form.Control.Feedback>
                      ) : null}
                    </Form.Group>
                  ) : (
                    <>
                      {type === "select" && options ? (
                        <Form.Group className={containerClass}>
                          {label ? (
                            <Form.Label className={labelClassName}>
                              {label}
                            </Form.Label>
                          ) : null}
                          <Controller
                            control={control}
                            name={name}
                            render={({ field }) => (
                              <MobileSelect
                                {...field}
                                label={label}
                                className="react-select react-select-container"
                                options={options}
                                control={control}
                                placeholder={placeholder}
                                type="text"
                                isMulti={false}
                                defaultValue={options[0]}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                // onChange={onChange}
                              />
                            )}
                          />

                          {errors && errors[name] ? (
                            <Form.Control.Feedback type="invalid">
                              {errors[name]["message"]}
                            </Form.Control.Feedback>
                          ) : null}
                        </Form.Group>
                      ) : (
                        <Form.Group className={containerClass}>
                          {label ? (
                            <Form.Label className={labelClassName}>
                              {label}
                            </Form.Label>
                          ) : null}
                          <Form.Control
                            type={type}
                            placeholder={placeholder}
                            name={name}
                            id={name}
                            as={comp}
                            ref={(r: HTMLInputElement) => {
                              const registered = register
                                ? register(name)
                                : undefined;
                              registered?.ref?.(r);
                              if (refCallback) refCallback(r);
                            }}
                            className={className}
                            isInvalid={errors && errors[name] ? true : false}
                            {...(register ? register(name) : {})}
                            rows={rows}
                            {...otherProps}
                            autoComplete={name}
                            onChange={(e: any) => {
                              const registered = register
                                ? register(name)
                                : undefined;
                              registered?.onChange?.(e);
                              if (onChange) (onChange as any)(e);
                            }}
                            onFocus={handleFocus}
                            onBlur={(e: any) => {
                              const registered = register
                                ? register(name)
                                : undefined;
                              registered?.onBlur?.(e);
                              handleBlur();
                            }}
                          >
                            {children ? children : null}
                          </Form.Control>

                          {errors && errors[name] ? (
                            <Form.Control.Feedback type="invalid">
                              {errors[name]["message"]}
                            </Form.Control.Feedback>
                          ) : null}
                        </Form.Group>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default FormInput;
